// src/features/organisation/hooks/useReferenceData.ts
import { useState, useEffect } from 'react';
import { apiClient } from '../../../services/api/client';

interface ReferenceData {
  code: string;
  name: string;
  description?: string;
}

interface SiteType extends ReferenceData {
  isActive: boolean;
}

interface EmployeeStatus extends ReferenceData {
  isActive: boolean;
}

interface RoleScope extends ReferenceData {}

interface RoleCategory extends ReferenceData {}

interface CertificationType extends ReferenceData {
  validityPeriodMonths?: number;
}

interface AssignmentType {
  code: string;
  description: string;
}

// Hook générique pour les données de référence
const useReferenceDataGeneric = <T extends ReferenceData>(endpoint: string) => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiClient.get<T[]>(endpoint);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        console.error(`Error fetching ${endpoint}:`, err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [endpoint]);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiClient.get<T[]>(endpoint);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      console.error(`Error refreshing ${endpoint}:`, err);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refresh };
};

// Hook pour les types de site
export const useSiteTypes = () => {
  const { data, loading, error, refresh } = useReferenceDataGeneric<SiteType>('/reference/site-types');

  return {
    siteTypes: data,
    loading,
    error,
    refresh,
  };
};

// Hook pour les statuts d'employé
export const useEmployeeStatuses = () => {
  const { data, loading, error, refresh } = useReferenceDataGeneric<EmployeeStatus>('/reference/employee-statuses');

  return {
    employeeStatuses: data,
    loading,
    error,
    refresh,
  };
};

// Hook pour les portées de rôle
export const useRoleScopes = () => {
  const { data, loading, error, refresh } = useReferenceDataGeneric<RoleScope>('/reference/role-scopes');

  return {
    roleScopes: data,
    loading,
    error,
    refresh,
  };
};

// Hook pour les catégories de rôle
export const useRoleCategories = () => {
  const { data, loading, error, refresh } = useReferenceDataGeneric<RoleCategory>('/reference/role-categories');

  return {
    roleCategories: data,
    loading,
    error,
    refresh,
  };
};

// Hook pour les types de certification
export const useCertificationTypes = () => {
  const { data, loading, error, refresh } = useReferenceDataGeneric<CertificationType>('/reference/certification-types');

  return {
    certificationTypes: data,
    loading,
    error,
    refresh,
  };
};

// Hook pour les types d'assignation multi-site
export const useAssignmentTypes = () => {
  const { data, loading, error, refresh } = useReferenceDataGeneric<AssignmentType>('/reference/assignment-types');

  return {
    assignmentTypes: data,
    loading,
    error,
    refresh,
  };
};

// Hook combiné pour charger toutes les données de référence en une fois (optionnel)
export const useAllReferenceData = () => {
  const { siteTypes, loading: loadingSiteTypes, error: errorSiteTypes } = useSiteTypes();
  const { employeeStatuses, loading: loadingEmployeeStatuses, error: errorEmployeeStatuses } = useEmployeeStatuses();
  const { roleScopes, loading: loadingRoleScopes, error: errorRoleScopes } = useRoleScopes();
  const { roleCategories, loading: loadingRoleCategories, error: errorRoleCategories } = useRoleCategories();
  const { certificationTypes, loading: loadingCertificationTypes, error: errorCertificationTypes } = useCertificationTypes();

  const loading = loadingSiteTypes || loadingEmployeeStatuses || loadingRoleScopes || loadingRoleCategories || loadingCertificationTypes;
  const error = errorSiteTypes || errorEmployeeStatuses || errorRoleScopes || errorRoleCategories || errorCertificationTypes;

  return {
    siteTypes,
    employeeStatuses,
    roleScopes,
    roleCategories,
    certificationTypes,
    loading,
    error,
  };
};

export default {
  useSiteTypes,
  useEmployeeStatuses,
  useRoleScopes,
  useRoleCategories,
  useCertificationTypes,
  useAssignmentTypes,
  useAllReferenceData,
};